/**
 * PLC 代码服务
 * 提供代码解析、格式化、验证等功能
 */

import type {
  PlcLanguageType,
  ParsedCodeBlock,
  PLC_LANGUAGES
} from '../types';

// ============================================
// 代码块解析
// ============================================

/** Markdown 代码块正则 - 匹配 ```language 或 ````language */
const CODE_BLOCK_REGEX = /```(`)?(\w*)\n([\s\S]*?)```(`)?/g;

/** PLC 语言别名映射 */
const LANGUAGE_ALIASES: Readonly<Record<string, PlcLanguageType>> = {
  // ST 别名
  st: 'st',
  structuredtext: 'st',
  'structured-text': 'st',
  pascal: 'st',
  iec61131: 'st',
  plc: 'st',
  
  // IL 别名
  il: 'il',
  instructionlist: 'il',
  'instruction-list': 'il',
  
  // LAD 别名
  lad: 'lad',
  ladder: 'lad',
  ladderdiagram: 'lad',
  'ladder-diagram': 'lad',
  
  // FBD 别名
  fbd: 'fbd',
  functionblock: 'fbd',
  'function-block': 'fbd',
  functionblockdiagram: 'fbd'
};

/**
 * 从语言标识符解析 PLC 语言类型
 */
const parseLanguageId = (langId: string): PlcLanguageType | 'unknown' => {
  const normalized = langId.toLowerCase().trim().replace(/[_\s]/g, '');
  return LANGUAGE_ALIASES[normalized] ?? 'unknown';
};

/**
 * 从文本中解析所有代码块
 */
export const parseCodeBlocks = (text: string): readonly ParsedCodeBlock[] => {
  const blocks: ParsedCodeBlock[] = [];
  let match: RegExpExecArray | null;
  
  // 重置正则状态
  CODE_BLOCK_REGEX.lastIndex = 0;
  
  while ((match = CODE_BLOCK_REGEX.exec(text)) !== null) {
    const langId = match[2] || '';
    const content = match[3] || '';
    const language = parseLanguageId(langId);
    
    blocks.push({
      language,
      content: content.trim(),
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }
  
  return blocks;
};

/**
 * 从文本中提取第一个 PLC 代码块
 * 优先返回已识别语言的代码块
 */
export const extractFirstPlcCode = (
  text: string,
  preferredLanguage?: PlcLanguageType
): ParsedCodeBlock | null => {
  const blocks = parseCodeBlocks(text);
  
  if (blocks.length === 0) {
    return null;
  }
  
  // 优先匹配指定语言
  if (preferredLanguage) {
    const preferred = blocks.find(b => b.language === preferredLanguage);
    if (preferred) {
      return preferred;
    }
  }
  
  // 其次匹配任何已识别的 PLC 语言
  const knownPlc = blocks.find(b => b.language !== 'unknown');
  if (knownPlc) {
    return knownPlc;
  }
  
  // 最后返回第一个代码块
  return blocks[0];
};

// ============================================
// 代码格式化
// ============================================

/** ST 语言关键字（用于格式化） */
const ST_KEYWORDS: readonly string[] = [
  'PROGRAM', 'END_PROGRAM',
  'FUNCTION', 'END_FUNCTION',
  'FUNCTION_BLOCK', 'END_FUNCTION_BLOCK',
  'VAR', 'VAR_INPUT', 'VAR_OUTPUT', 'VAR_IN_OUT', 'VAR_GLOBAL', 'END_VAR',
  'IF', 'THEN', 'ELSIF', 'ELSE', 'END_IF',
  'CASE', 'OF', 'END_CASE',
  'FOR', 'TO', 'BY', 'DO', 'END_FOR',
  'WHILE', 'END_WHILE',
  'REPEAT', 'UNTIL', 'END_REPEAT',
  'RETURN', 'EXIT', 'CONTINUE',
  'TRUE', 'FALSE',
  'AND', 'OR', 'NOT', 'XOR', 'MOD',
  'BOOL', 'INT', 'DINT', 'REAL', 'LREAL', 'STRING', 'TIME', 'DATE'
];

/** IL 语言指令 */
const IL_INSTRUCTIONS: readonly string[] = [
  'LD', 'LDN', 'ST', 'STN', 'S', 'R',
  'AND', 'ANDN', 'OR', 'ORN', 'XOR', 'XORN', 'NOT',
  'ADD', 'SUB', 'MUL', 'DIV', 'MOD',
  'GT', 'GE', 'EQ', 'NE', 'LE', 'LT',
  'JMP', 'JMPC', 'JMPCN', 'CAL', 'CALC', 'CALCN', 'RET', 'RETC', 'RETCN'
];

/**
 * 格式化 ST 代码
 * - 统一缩进
 * - 关键字大写
 */
export const formatStCode = (code: string): string => {
  const lines = code.split('\n');
  let indentLevel = 0;
  const indentSize = 4;
  const indentChar = ' ';
  
  const formattedLines = lines.map(line => {
    let trimmed = line.trim();
    
    if (!trimmed) {
      return '';
    }
    
    // 关键字大写
    ST_KEYWORDS.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      trimmed = trimmed.replace(regex, keyword);
    });
    
    // 减少缩进的关键字
    const decreaseIndentPatterns = [
      /^END_/i,
      /^ELSIF\b/i,
      /^ELSE\b/i,
      /^UNTIL\b/i
    ];
    
    const shouldDecreaseBefore = decreaseIndentPatterns.some(p => p.test(trimmed));
    if (shouldDecreaseBefore && indentLevel > 0) {
      indentLevel--;
    }
    
    const indent = indentChar.repeat(indentLevel * indentSize);
    const result = indent + trimmed;
    
    // 增加缩进的关键字
    const increaseIndentPatterns = [
      /^PROGRAM\b/i,
      /^FUNCTION\b/i,
      /^FUNCTION_BLOCK\b/i,
      /^VAR\b/i,
      /^VAR_INPUT\b/i,
      /^VAR_OUTPUT\b/i,
      /^VAR_IN_OUT\b/i,
      /^IF\b.*THEN\s*$/i,
      /^ELSIF\b.*THEN\s*$/i,
      /^ELSE\s*$/i,
      /^CASE\b/i,
      /^FOR\b.*DO\s*$/i,
      /^WHILE\b.*DO\s*$/i,
      /^REPEAT\s*$/i
    ];
    
    const shouldIncreaseAfter = increaseIndentPatterns.some(p => p.test(trimmed));
    if (shouldIncreaseAfter) {
      indentLevel++;
    }
    
    return result;
  });
  
  return formattedLines.join('\n');
};

/**
 * 格式化 IL 代码
 * - 统一缩进
 * - 指令大写
 */
export const formatIlCode = (code: string): string => {
  const lines = code.split('\n');
  
  const formattedLines = lines.map(line => {
    let trimmed = line.trim();
    
    if (!trimmed) {
      return '';
    }
    
    // 标签行保持原样
    if (trimmed.endsWith(':')) {
      return trimmed;
    }
    
    // 指令大写
    IL_INSTRUCTIONS.forEach(instr => {
      const regex = new RegExp(`^${instr}\\b`, 'i');
      trimmed = trimmed.replace(regex, instr);
    });
    
    // 非标签行添加缩进
    return '    ' + trimmed;
  });
  
  return formattedLines.join('\n');
};

/**
 * 根据语言类型格式化代码
 */
export const formatPlcCode = (
  code: string,
  language: PlcLanguageType
): string => {
  switch (language) {
    case 'st':
      return formatStCode(code);
    case 'il':
      return formatIlCode(code);
    case 'lad':
    case 'fbd':
      // 图形化语言暂不格式化
      return code;
    default:
      return code;
  }
};

// ============================================
// 代码验证
// ============================================

/** 验证结果 */
export interface ValidationResult {
  readonly isValid: boolean;
  readonly errors: readonly ValidationError[];
  readonly warnings: readonly ValidationWarning[];
}

export interface ValidationError {
  readonly line: number;
  readonly column: number;
  readonly message: string;
  readonly code: string;
}

export interface ValidationWarning {
  readonly line: number;
  readonly column: number;
  readonly message: string;
  readonly code: string;
}

/**
 * 简单的 ST 代码验证
 * 注意：这只是基础语法检查，不是完整编译器
 */
export const validateStCode = (code: string): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const lines = code.split('\n');
  
  // 检查配对关键字
  const pairs: Array<{ open: RegExp; close: RegExp; name: string }> = [
    { open: /\bPROGRAM\b/i, close: /\bEND_PROGRAM\b/i, name: 'PROGRAM' },
    { open: /\bFUNCTION\b/i, close: /\bEND_FUNCTION\b/i, name: 'FUNCTION' },
    { open: /\bFUNCTION_BLOCK\b/i, close: /\bEND_FUNCTION_BLOCK\b/i, name: 'FUNCTION_BLOCK' },
    { open: /\bVAR\b/i, close: /\bEND_VAR\b/i, name: 'VAR' },
    { open: /\bIF\b/i, close: /\bEND_IF\b/i, name: 'IF' },
    { open: /\bCASE\b/i, close: /\bEND_CASE\b/i, name: 'CASE' },
    { open: /\bFOR\b/i, close: /\bEND_FOR\b/i, name: 'FOR' },
    { open: /\bWHILE\b/i, close: /\bEND_WHILE\b/i, name: 'WHILE' },
    { open: /\bREPEAT\b/i, close: /\bEND_REPEAT\b/i, name: 'REPEAT' }
  ];
  
  pairs.forEach(({ open, close, name }) => {
    let openCount = 0;
    let closeCount = 0;
    
    lines.forEach(line => {
      // 忽略注释
      const noComment = line.replace(/\(\*.*?\*\)/g, '').replace(/\/\/.*$/g, '');
      
      const openMatches = noComment.match(open);
      const closeMatches = noComment.match(close);
      
      if (openMatches) openCount += openMatches.length;
      if (closeMatches) closeCount += closeMatches.length;
    });
    
    if (openCount > closeCount) {
      errors.push({
        line: 1,
        column: 1,
        message: `缺少 END_${name}，${name} 块未闭合`,
        code: 'E001'
      });
    } else if (closeCount > openCount) {
      errors.push({
        line: 1,
        column: 1,
        message: `多余的 END_${name}，没有匹配的 ${name}`,
        code: 'E002'
      });
    }
  });
  
  // 检查语句结尾分号
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // 跳过空行、注释、块开始/结束
    if (!trimmed) return;
    if (trimmed.startsWith('//') || trimmed.startsWith('(*')) return;
    if (/^(PROGRAM|FUNCTION|VAR|IF|ELSE|ELSIF|CASE|FOR|WHILE|REPEAT|END_)/i.test(trimmed)) return;
    if (/^[A-Za-z_]\w*\s*:/.test(trimmed) && !trimmed.includes(':=')) return; // 变量声明
    
    // 赋值语句应以分号结尾
    if (/:=/.test(trimmed) && !trimmed.endsWith(';')) {
      warnings.push({
        line: index + 1,
        column: trimmed.length,
        message: '赋值语句建议以分号结尾',
        code: 'W001'
      });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * 根据语言类型验证代码
 */
export const validatePlcCode = (
  code: string,
  language: PlcLanguageType
): ValidationResult => {
  switch (language) {
    case 'st':
      return validateStCode(code);
    case 'il':
    case 'lad':
    case 'fbd':
      // 暂不支持验证
      return { isValid: true, errors: [], warnings: [] };
    default:
      return { isValid: true, errors: [], warnings: [] };
  }
};

// ============================================
// 代码模板
// ============================================

/** ST 程序模板 */
export const ST_PROGRAM_TEMPLATE = `PROGRAM Main
VAR
    (* 在这里声明变量 *)
    bStart : BOOL := FALSE;
    bStop : BOOL := FALSE;
    bRunning : BOOL := FALSE;
END_VAR

(* 主程序逻辑 *)
IF bStart AND NOT bStop THEN
    bRunning := TRUE;
ELSIF bStop THEN
    bRunning := FALSE;
END_IF;

END_PROGRAM
`;

/** ST 功能块模板 */
export const ST_FUNCTION_BLOCK_TEMPLATE = `FUNCTION_BLOCK FB_Motor
VAR_INPUT
    bEnable : BOOL;
    rSpeed : REAL;
END_VAR
VAR_OUTPUT
    bRunning : BOOL;
    bError : BOOL;
END_VAR
VAR
    (* 内部变量 *)
    rCurrentSpeed : REAL := 0.0;
END_VAR

(* 功能块逻辑 *)
IF bEnable THEN
    rCurrentSpeed := rSpeed;
    bRunning := TRUE;
    bError := FALSE;
ELSE
    rCurrentSpeed := 0.0;
    bRunning := FALSE;
END_IF;

END_FUNCTION_BLOCK
`;

/** IL 程序模板 */
export const IL_PROGRAM_TEMPLATE = `(* 指令表示例程序 *)
START:
    LD      bStart
    ANDN    bStop
    ST      bRunning
    
    LD      bStop
    JMPC    STOP_MOTOR
    
    RET

STOP_MOTOR:
    LD      FALSE
    ST      bRunning
    RET
`;

/**
 * 获取代码模板
 */
export const getCodeTemplate = (
  language: PlcLanguageType,
  templateType: 'program' | 'function_block' = 'program'
): string => {
  switch (language) {
    case 'st':
      return templateType === 'function_block' 
        ? ST_FUNCTION_BLOCK_TEMPLATE 
        : ST_PROGRAM_TEMPLATE;
    case 'il':
      return IL_PROGRAM_TEMPLATE;
    case 'lad':
    case 'fbd':
      return '(* 图形化语言请使用专业 PLC 编程软件 *)';
    default:
      return '';
  }
};

// ============================================
// 变量解析（用于测试功能）
// ============================================

import type { 
  PlcVariable, 
  PlcDataType, 
  VariableDirection, 
  CodeParseResult 
} from '../types/testTypes';

/** 支持的数据类型列表 */
const SUPPORTED_DATA_TYPES: readonly PlcDataType[] = [
  'BOOL', 'INT', 'DINT', 'REAL', 'LREAL', 
  'STRING', 'TIME', 'BYTE', 'WORD', 'DWORD'
];

/** 变量声明块正则模式 */
const VAR_BLOCK_PATTERNS: Record<VariableDirection, RegExp> = {
  INPUT: /VAR_INPUT\s*([\s\S]*?)\s*END_VAR/gi,
  OUTPUT: /VAR_OUTPUT\s*([\s\S]*?)\s*END_VAR/gi,
  IN_OUT: /VAR_IN_OUT\s*([\s\S]*?)\s*END_VAR/gi,
  LOCAL: /\bVAR\b(?!_)\s*([\s\S]*?)\s*END_VAR/gi
};

/** 单个变量声明正则：变量名 : 类型 [:= 初始值] ; // 注释 */
const VARIABLE_PATTERN = /(\w+)\s*:\s*(\w+)(?:\s*:=\s*([^;]+?))?(?:\s*;)(?:\s*(?:\/\/|--|\(\*)\s*([^\n\r\*]*))?/gi;

/**
 * 从变量声明块内容中解析变量
 */
const parseVariablesFromBlock = (
  blockContent: string,
  direction: VariableDirection
): PlcVariable[] => {
  const variables: PlcVariable[] = [];
  const pattern = new RegExp(VARIABLE_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;
  
  while ((match = pattern.exec(blockContent)) !== null) {
    const [, name, dataTypeRaw, initialValue, comment] = match;
    const dataType = dataTypeRaw.toUpperCase() as PlcDataType;
    
    // 只处理支持的数据类型
    if (SUPPORTED_DATA_TYPES.includes(dataType)) {
      variables.push({
        name: name.trim(),
        dataType,
        direction,
        initialValue: initialValue?.trim(),
        comment: comment?.trim()
      });
    }
  }
  
  return variables;
};

/**
 * 解析 PLC 代码中的变量声明
 * 支持 ST 和 IL 语言
 */
export const parseVariables = (
  code: string, 
  language: PlcLanguageType
): CodeParseResult => {
  if (!code || code.trim().length === 0) {
    return {
      success: false,
      variables: [],
      inputs: [],
      outputs: [],
      errors: ['代码为空'],
      language
    };
  }
  
  // 图形化语言尝试 JSON 解析
  if (language === 'lad' || language === 'fbd') {
    try {
      const data = JSON.parse(code);
      if (data.variables && Array.isArray(data.variables)) {
        const variables: PlcVariable[] = data.variables.map((v: Record<string, unknown>) => ({
          name: String(v.name ?? ''),
          dataType: (String(v.dataType ?? 'BOOL').toUpperCase()) as PlcDataType,
          direction: (String(v.direction ?? 'LOCAL').toUpperCase()) as VariableDirection,
          initialValue: v.initialValue != null ? String(v.initialValue) : undefined,
          comment: v.comment != null ? String(v.comment) : undefined
        }));
        
        return {
          success: true,
          variables,
          inputs: variables.filter(v => v.direction === 'INPUT' || v.direction === 'IN_OUT'),
          outputs: variables.filter(v => v.direction === 'OUTPUT' || v.direction === 'IN_OUT'),
          language
        };
      }
    } catch {
      // 不是 JSON，继续用正则解析
    }
  }
  
  // ST/IL 使用正则解析
  const allVariables: PlcVariable[] = [];
  const errors: string[] = [];
  
  try {
    const directions: VariableDirection[] = ['INPUT', 'OUTPUT', 'IN_OUT', 'LOCAL'];
    
    for (const direction of directions) {
      const pattern = new RegExp(VAR_BLOCK_PATTERNS[direction].source, 'gi');
      let blockMatch: RegExpExecArray | null;
      
      while ((blockMatch = pattern.exec(code)) !== null) {
        const blockContent = blockMatch[1];
        const parsedVars = parseVariablesFromBlock(blockContent, direction);
        allVariables.push(...parsedVars);
      }
    }
    
    const inputs = allVariables.filter(v => v.direction === 'INPUT' || v.direction === 'IN_OUT');
    const outputs = allVariables.filter(v => v.direction === 'OUTPUT' || v.direction === 'IN_OUT');
    
    return {
      success: true,
      variables: allVariables,
      inputs,
      outputs,
      language
    };
  } catch (error) {
    errors.push(`解析错误: ${error instanceof Error ? error.message : '未知错误'}`);
    return {
      success: false,
      variables: [],
      inputs: [],
      outputs: [],
      errors,
      language
    };
  }
};

/**
 * 验证变量值是否符合数据类型
 */
export const validateVariableValue = (value: string, dataType: PlcDataType): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true; // 空值允许
  
  switch (dataType) {
    case 'BOOL':
      return /^(TRUE|FALSE|0|1)$/i.test(trimmed);
    case 'INT':
      const intVal = parseInt(trimmed, 10);
      return !isNaN(intVal) && intVal >= -32768 && intVal <= 32767;
    case 'DINT':
      return /^-?\d+$/.test(trimmed);
    case 'REAL':
    case 'LREAL':
      return /^-?\d+\.?\d*([eE][+-]?\d+)?$/.test(trimmed);
    case 'STRING':
      return true;
    case 'TIME':
      return /^T#/i.test(trimmed) || /^\d+$/i.test(trimmed);
    case 'BYTE':
      const byteVal = parseInt(trimmed, 10);
      return !isNaN(byteVal) && byteVal >= 0 && byteVal <= 255;
    case 'WORD':
      const wordVal = parseInt(trimmed, 10);
      return !isNaN(wordVal) && wordVal >= 0 && wordVal <= 65535;
    case 'DWORD':
      return /^\d+$/.test(trimmed);
    default:
      return true;
  }
};

/**
 * 将字符串值解析为对应类型
 */
export const parseVariableValue = (value: string, dataType: PlcDataType): unknown => {
  const trimmed = value.trim();
  
  switch (dataType) {
    case 'BOOL':
      return /^(TRUE|1)$/i.test(trimmed);
    case 'INT':
    case 'DINT':
    case 'BYTE':
    case 'WORD':
    case 'DWORD':
      return parseInt(trimmed, 10) || 0;
    case 'REAL':
    case 'LREAL':
      return parseFloat(trimmed) || 0;
    case 'STRING':
      return trimmed;
    case 'TIME':
      return trimmed;
    default:
      return trimmed;
  }
};

/**
 * 将值格式化为显示字符串
 */
export const formatVariableValue = (value: unknown, dataType: PlcDataType): string => {
  if (value === null || value === undefined) {
    return '';
  }
  
  switch (dataType) {
    case 'BOOL':
      return value ? 'TRUE' : 'FALSE';
    case 'REAL':
    case 'LREAL':
      return typeof value === 'number' ? value.toFixed(2) : String(value);
    default:
      return String(value);
  }
};

// ============================================
// 导出服务对象（便于统一调用）
// ============================================

export const plcCodeService = {
  parseCodeBlocks,
  extractFirstPlcCode,
  formatPlcCode,
  formatStCode,
  formatIlCode,
  validatePlcCode,
  validateStCode,
  getCodeTemplate,
  parseLanguageId,
  // 新增变量解析相关
  parseVariables,
  validateVariableValue,
  parseVariableValue,
  formatVariableValue
} as const;